import Link from "next/link";
import { getLanguages, getCategoriesByLanguage } from "../lib/registry";
import LanguageIcon from "./LanguageIcon";

export default function Footer() {
  const languages = getLanguages().filter(lang => lang.status !== 'coming-soon');
  const totalRules = languages.reduce((acc, lang) => acc + lang.ruleCount, 0);

  return (
    <footer>
      {/* Registry Explorer - warm light background, feels like content not footer */}
      <div className="bg-gradient-to-b from-[#fdfbf3] to-[#f7f3e8] border-t border-[#e7e0cf]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs font-semibold tracking-widest text-lime-600 uppercase mb-1">Registry</p>
              <h3 className="text-xl font-bold text-gray-900">{totalRules} Security Rules</h3>
            </div>
            <Link
              href="/registry"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
            >
              View full registry
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {languages.map((lang) => {
              const categories = getCategoriesByLanguage(lang.id);
              return (
                <div key={lang.id} className="bg-white rounded-xl p-5 shadow-sm border border-[#e7e0cf] hover:shadow-md transition-shadow">
                  <Link href={`/registry/${lang.id}`} className="flex items-center gap-2.5 mb-4 group">
                    <div className={`bg-gradient-to-br ${lang.color} p-2 rounded-lg flex items-center justify-center shadow-sm`}>
                      <LanguageIcon icon={lang.icon} className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-gray-900 font-semibold text-sm group-hover:text-lime-600 transition-colors">{lang.name}</span>
                      <span className="block text-xs text-gray-500">{lang.ruleCount} rules, {lang.categoryCount} categories</span>
                    </div>
                  </Link>
                  <div className="flex flex-wrap gap-1.5">
                    {categories.map((cat) => (
                      <Link
                        key={cat.id}
                        href={`/registry/${lang.id}/${cat.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-[#f7f3e8] border border-[#e7e0cf] rounded-full text-xs text-gray-600 hover:bg-[#ecfcca] hover:border-[#d8f999] hover:text-[#4b7d00] transition-colors"
                      >
                        {cat.name}
                        <span className="text-gray-600 font-medium">{cat.ruleCount}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main footer - dark */}
      <div className="bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-8">
            {/* Brand */}
            <div className="col-span-2 md:col-span-2">
              <Link href="/" className="text-white font-bold text-base">/code pathfinder.</Link>
              <p className="text-gray-400 text-sm mt-2 leading-relaxed">
                Precise security analysis developers trust
              </p>
              <div className="flex items-center gap-3 mt-4">
                <Link href="https://github.com/shivasurya/code-pathfinder" className="text-gray-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                  </svg>
                </Link>
                <Link href="https://twitter.com/sshivasurya" className="text-gray-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                </Link>
                <Link href="https://www.linkedin.com/in/shivasurya/" className="text-gray-400 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                </Link>
                <Link href="/blog/feed.xml" className="text-gray-400 hover:text-orange-400 transition-colors" title="RSS Feed" aria-label="RSS Feed">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M6.503 20.752c0 1.794-1.456 3.248-3.251 3.248-1.796 0-3.252-1.454-3.252-3.248 0-1.794 1.456-3.248 3.252-3.248 1.795 0 3.251 1.454 3.251 3.248zm-6.503-12.572v4.811c6.05.062 10.96 4.966 11.022 11.009h4.817c-.062-8.71-7.118-15.758-15.839-15.82zm0-8.18v4.819c12.951.115 23.357 10.71 23.497 23.625h4.503c-.145-15.842-13.058-28.754-28-28.444z"/>
                  </svg>
                </Link>
                <Link
                  href="https://github.com/shivasurya/code-pathfinder"
                  className="pl-3 border-l border-gray-700 text-gray-400 hover:text-white transition-colors flex items-center gap-1.5 text-xs"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .587l3.668 7.431 8.332 1.151-6.064 5.828 1.48 8.279L12 19.896l-7.417 3.937 1.482-8.279L0 9.726l8.332-1.151z"/></svg>
                  Star on GitHub
                </Link>
              </div>
            </div>

            {/* Product */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Product</h3>
              <ul className="space-y-2">
                <li><Link href="/secureflow-ai" className="text-gray-400 text-sm hover:text-white transition-colors">SecureFlow AI</Link></li>
                <li><Link href="/mcp" className="text-gray-400 text-sm hover:text-white transition-colors">MCP Server</Link></li>
                <li><Link href="/docs/mcp/docker-mcp" className="text-gray-400 text-sm hover:text-white transition-colors">Docker MCP</Link></li>
                <li><Link href="/docs" className="text-gray-400 text-sm hover:text-white transition-colors">Documentation</Link></li>
                <li><Link href="/registry" className="text-gray-400 text-sm hover:text-white transition-colors">Registry</Link></li>
              </ul>
            </div>

            {/* Resources */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Resources</h3>
              <ul className="space-y-2">
                <li><Link href="/docs" className="text-gray-400 text-sm hover:text-white transition-colors">Docs</Link></li>
                <li><Link href="/blog" className="text-gray-400 text-sm hover:text-white transition-colors">Blog</Link></li>
                <li><Link href="/docs/changelog" className="text-gray-400 text-sm hover:text-white transition-colors">Changelog</Link></li>
                <li><Link href="https://github.com/shivasurya/code-pathfinder" className="text-gray-400 text-sm hover:text-white transition-colors">GitHub</Link></li>
              </ul>
            </div>

            {/* Community */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Community</h3>
              <ul className="space-y-2">
                <li><Link href="https://github.com/shivasurya/code-pathfinder/blob/main/CONTRIBUTING.md" className="text-gray-400 text-sm hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">Contributing</Link></li>
                <li><Link href="https://github.com/shivasurya/code-pathfinder/blob/main/CODE_OF_CONDUCT.md" className="text-gray-400 text-sm hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">Code of Conduct</Link></li>
                <li><Link href="https://github.com/shivasurya/code-pathfinder/issues" className="text-gray-400 text-sm hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">Report Bug</Link></li>
                <li><Link href="https://github.com/shivasurya/code-pathfinder/discussions" className="text-gray-400 text-sm hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">Discussions</Link></li>
              </ul>
            </div>

            {/* Popular Rules - fills right side + extra SEO links */}
            <div>
              <h3 className="text-white font-semibold text-sm mb-3">Popular Rules</h3>
              <ul className="space-y-2">
                <li><Link href="/registry/python/django/PYTHON-DJANGO-SEC-001" className="text-gray-400 text-sm hover:text-white transition-colors">Django SQL Injection</Link></li>
                <li><Link href="/registry/python/flask/PYTHON-FLASK-SEC-002" className="text-gray-400 text-sm hover:text-white transition-colors">Flask SSTI</Link></li>
                <li><Link href="/registry/python/lang/PYTHON-LANG-SEC-001" className="text-gray-400 text-sm hover:text-white transition-colors">Dangerous eval()</Link></li>
                <li><Link href="/registry/docker/security/DOCKER-SEC-001" className="text-gray-400 text-sm hover:text-white transition-colors">Missing USER</Link></li>
                <li><Link href="/registry/python/cryptography/PYTHON-CRYPTO-SEC-001" className="text-gray-400 text-sm hover:text-white transition-colors">Weak Cipher (RC4)</Link></li>
                <li><Link href="/registry/python/jwt/PYTHON-JWT-SEC-001" className="text-gray-400 text-sm hover:text-white transition-colors">JWT Hardcoded Secret</Link></li>
              </ul>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t border-gray-800/60 flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-gray-400">
            <div>
              © {new Date().getFullYear()} Code Pathfinder. Licensed under{" "}
              <Link
                href="https://github.com/shivasurya/code-pathfinder/blob/main/LICENSE"
                className="text-gray-300 hover:text-white transition-colors underline underline-offset-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                Apache-2.0
              </Link>
            </div>
            <div className="flex items-center gap-1.5 text-gray-400">
              Built with
              <span className="text-red-400">&#10084;</span>
              from Waterloo, Canada
              <svg className="w-4 h-3 inline-block" viewBox="0 0 40 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="10" height="27" fill="#EF4444"/>
                <rect x="10" width="20" height="27" fill="white"/>
                <rect x="30" width="10" height="27" fill="#EF4444"/>
                <path d="M20 4l1.5 3.5L20 9l-1.5-1.5L20 4zM16 10l2-1 1 2-2 1-1-2zM24 10l-2-1-1 2 2 1 1-2zM17 13l1.5 1 .5 2.5L20 15l1-1.5.5-2.5 1.5-1-1-2H18l-1 2zM15 14l1.5-.5.5 2-1.5.5-.5-2zM25 14l-1.5-.5-.5 2 1.5.5.5-2zM18 18h4v3h-4z" fill="#EF4444"/>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
